/**
 * The role model, mirrored from the API for UI adaptation only.
 *
 * **Enforcement is the API's, always.** Never decide anything from the token — it carries
 * `{staffId}` and nothing else, and role and Casino are re-resolved server-side on every request.
 * `GET /v1/me` is the authority for what the UI should offer; re-fetch it after any surprising
 * 403, because an admin can change a role mid-session and the login snapshot goes stale.
 */
export type StaffRole = "platform_admin" | "manager" | "technician" | "cashier";

/**
 * The workspace a role lands in. Four jobs, not one console with a filtered nav — a technician and
 * a cashier share exactly one route, so a shared shell greys most of the menu for most people and
 * has no dashboard that means anything to either.
 *
 * `activity` is the fifth entry and it is NOT a fifth job: it is `platform_admin`'s second
 * Casino-less surface, the platform-wide attribution trail. It is a workspace here for one reason —
 * this union is what `WORKSPACE_PATH`, `WORKSPACE_ALLOW`, `WORKSPACE_ORDER` and `AppShell`'s icon
 * and micro-label tables are all keyed by, so anything reachable from the rail has to be one, and
 * an entry added to the union is an entry the compiler then demands in every one of those tables.
 */
export type Workspace = "kiosks" | "cage" | "day" | "tenancy" | "activity";

export const HOME: Record<StaffRole, Workspace> = {
  technician: "kiosks",
  cashier: "cage",
  manager: "day",
  /**
   * Admin's home is tenancy and people — the two route groups only it can reach. The cash routes
   * it can technically reach are for cover and emergencies, and are entered through break-glass
   * rather than sitting in a superset menu. See design doc §2.
   */
  platform_admin: "tenancy",
};

/**
 * `platform_admin` passes every check, including lists it does not appear on. That is deliberate
 * (ADR-0005, amended 2026-08-04) and is not to be "fixed" by adding it to each list — the
 * amendment traded prevention for attribution, and every admin action is recorded against its
 * staff id.
 */
export function can(role: StaffRole, allowed: readonly StaffRole[]): boolean {
  return role === "platform_admin" || allowed.includes(role);
}

/**
 * The allow-lists, copied from the API's orchestration modules. Kept here so a route guard reads
 * as data rather than as scattered conditionals — and so a drift between these and the API shows
 * up as a 403 the UI can explain, not as a mystery.
 */
export const ALLOW = {
  authorizeOperation: ["technician"],
  cancelOperation: ["technician"],
  /**
   * Manager, deliberately not technician. The technician was standing at the machine when the
   * notes moved; letting them also close the record inverts ADR-0005's rule against one person
   * moving notes and adjusting the books, in the one case with no machine evidence to check them
   * against. Disable it for a technician WITH THE REASON — hiding it makes the app look broken.
   */
  closeOperation: ["manager"],
  listOperations: ["manager", "cashier", "technician"],
  listKiosks: ["manager", "technician"],
  registerKiosk: ["manager"],
  issueActivation: ["manager", "technician"],
  revokeCredential: ["manager"],
  cageWithdrawal: ["cashier"],
  listPayoutExceptions: ["manager", "cashier"],
  resolveException: ["manager"],
  /** `GET /v1/payout/stranded` (review M5) — `manager` alone; `cashier` is excluded because it is a
   *  follow-up worklist, not a floor task (`list-stranded-payouts.ts`). */
  listStrandedPayouts: ["manager"],
  reconcile: ["manager", "cashier"],
  signoff: ["manager"],
  /** Empty list = admin only. */
  casinoAdmin: [],
  staffAdmin: [],
  /**
   * `GET /v1/activity` — empty list, so admin only, and its own entry rather than a second use of
   * `casinoAdmin`. The route's own doc comment is explicit that this IS the superuser surface the
   * trail exists to keep reviewable, "so there is no scoped role to admit here at all": if a
   * manager is ever admitted to their own Casino's slice of the trail, that widening belongs on
   * this list and must not silently widen Casino administration along with it.
   */
  activityRead: [],
} satisfies Record<string, readonly StaffRole[]>;

/**
 * The eight irreversible actions and how much friction each earns.
 *
 * Friction follows what has **no other check**, not what carries the largest number. A cage
 * withdrawal hands over real notes and sits in the middle tier because the player is standing
 * there, watched the debit and counts the money — ADR-0005 says that witness is the control. The
 * tenancy override moves nothing and gets the heaviest tier, because it has no witness at all and
 * its damage lands at a Casino whose operator is not in the room.
 *
 * Payout-exception resolution earns the heaviest tier too, and for a sharper reason than tenancy.
 * `backoffice-api`'s `resolveException` (`services/backoffice-api/src/orchestration/resolve-
 * exception.ts`) computes the credit straight from what the operator types — `intent.amount -
 * attested`, or `0` / `intent.amount - dispensedAmount` for the other attestation shape — and the
 * only server-side check is that the number is an integer in range. Nothing corroborates the
 * figure itself: `unknown_dispense` exists precisely because the machine's own count is
 * unreliable, which is why a human counts the cassette instead of trusting the device. That is
 * "no other check" in its purest form, and it is worse than tenancy — an anonymous player
 * (ADR-0004) has no identity to appeal an under-credit with, where a Casino operator who notices a
 * bad tenancy change can at least notice and complain. `type-to-confirm`'s phrase for this action
 * is the credit amount itself, the one value this flow cannot otherwise check — not an opaque id.
 *
 * Revoking a kiosk credential also earns `type-to-confirm`, and the precedent for it is
 * `casinoTenancy`, **not** `resolveException` — the two `type-to-confirm` actions above earn the
 * tier for two different reasons, and this one matches the first, not the second. Exception
 * resolution earns its tier because the typed AMOUNT is a value the server cannot check at all.
 * A kiosk id is not that: `device-svc`'s `revoke-credential.ts` throws `CREDENTIAL_NOT_FOUND` when
 * a call matches nothing, and the action is recoverable anyway — re-activating a wrongly-revoked
 * kiosk mints it a fresh credential. What the server cannot catch is a **valid-but-wrong
 * neighbour** among forty kiosks on one screen — kiosk_1 read for kiosk_12 — and that wrong-target
 * risk, not an unverifiable number, is exactly what a typed phrase exists to catch. Same tier as
 * tenancy for the same reason as tenancy: no witness in the room, and the phrase is the kiosk id
 * printed on the machine, never the opaque `credentialId` the request body actually carries.
 *
 * `registerKiosk` and `createCasino` both land at `review`, deliberately lighter than the three
 * `type-to-confirm` actions above — and the reason is the same for both, so they are stated once,
 * here. Both are **creates**: a mistake produces an EXTRA row, never a destroyed one, and both are
 * correctable after the fact — a wrongly-registered kiosk can be retired, a wrongly-created Casino
 * suspended. Neither carries the wrong-target risk that earns `type-to-confirm`: revoking a
 * credential or overriding tenancy means picking the right one of forty existing rows on a screen,
 * where a slip lands the action on a valid-but-wrong NEIGHBOUR. Registering a kiosk or creating a
 * Casino means naming a thing that does not exist yet — there is no neighbour to mistake it for,
 * only a typo to notice and fix by trying again. That is the whole distinction against
 * `revokeCredential`, which sits one tier heavier for exactly the risk these two do not carry.
 */
export type ConfirmTier = "type-to-confirm" | "review" | "acknowledge";

/**
 * `satisfies`, not a `Record<string, ConfirmTier>` annotation: the latter widens every access
 * (`CONFIRM.resolveException`) to `ConfirmTier | undefined` under `noUncheckedIndexedAccess`,
 * forcing every call site to handle an "undefined tier" that can never actually happen — the key
 * set here is closed, not open. `satisfies` keeps the literal key names so a typo'd or renamed key
 * is a compile error, while still checking every value is a `ConfirmTier`.
 */
export const CONFIRM = {
  casinoTenancy: "type-to-confirm",
  cageWithdrawal: "review",
  resolveException: "type-to-confirm",
  signoff: "review",
  activationCode: "acknowledge",
  revokeCredential: "type-to-confirm",
  registerKiosk: "review",
  createCasino: "review",
} satisfies Record<string, ConfirmTier>;

/** Rare, unrecoverable, and worth a password. Not the busiest action — that is the most rate-limited route. */
export const STEP_UP = ["signoff", "breakGlassEntry"] as const;
